import React, { useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { 
  FiArrowLeft, 
  FiUsers, 
  FiClock, 
  FiTrendingUp,
  FiShield,
  FiCheckCircle,
  FiAlertCircle,
  FiFlag,
  FiAward
} from 'react-icons/fi';
import { 
  FaFacebook, 
  FaInstagram, 
  FaTwitter, 
  FaWhatsapp, 
  FaTelegram, 
  FaTiktok,
  FaLinkedin,
  FaYoutube,
  FaCheckCircle
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import { fetchAccountById, clearSelectedAccount } from '../store/slices/accountSlice';
import { openModal } from '../store/slices/uiSlice';
import { GlassCard } from '../components/ui/GlassCard';
import toast from 'react-hot-toast';

export const AccountDetailsPage = () => {
  const { name } = useParams();
  const location = useLocation();
  const { id } = location.state || {};
  console.log('this is the id from location state', id);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedAccount: account, isLoading } = useSelector((state) => state.accounts);
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(fetchAccountById(id));
    return () => {
      dispatch(clearSelectedAccount());
    };
  }, [dispatch, name]);

  const platformIcons = {
    Facebook: FaFacebook,
    Instagram: FaInstagram,
    Twitter: FaTwitter,
    WhatsApp: FaWhatsapp,
    Telegram: FaTelegram,
    TikTok: FaTiktok,
    LinkedIn: FaLinkedin,
    YouTube: FaYoutube
  };

  const platformColors = {
    Facebook: 'from-blue-600 to-blue-400',
    Instagram: 'from-purple-600 to-pink-500',
    Twitter: 'from-sky-400 to-blue-500',
    WhatsApp: 'from-green-500 to-emerald-400',
    Telegram: 'from-blue-400 to-cyan-400',
    TikTok: 'from-gray-800 to-gray-600',
    LinkedIn: 'from-blue-700 to-blue-500',
    YouTube: 'from-red-600 to-red-400'
  };

  const handlePurchase = () => {
    if (!isAuthenticated) {
      toast.error('Please login to purchase this account');
      navigate('/login');
      return;
    }
    
    if (user.balance < account.price) {
      toast.error('Insufficient balance. Please deposit funds.');
      navigate('/deposit');
      return;
    }

    navigate(`/checkout/${account._id}`);
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://nitrologs.com';
  const pageUrl = typeof window !== 'undefined' ? window.location.href : `${siteUrl}/accounts/${id}`;
  const defaultImage = `${siteUrl}/nitrologs.png`;
  const pageTitle = account ? `${account.title} | Nitro Logs` : 'Account Details | Nitro Logs';
  const pageDescription = account?.description
    ? account.description.substring(0, 155)
    : `Verified ${account?.socialMediaType || 'social media'} account with real followers, secure purchase, and instant delivery.`;
  const productSchema = account ? {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: account.title,
    description: account.description || `Verified ${account.socialMediaType} social media account.`,
    sku: account._id,
    brand: {
      '@type': 'Brand',
      name: 'Nitro Logs'
    },
    offers: {
      '@type': 'Offer',
      url: pageUrl,
      priceCurrency: 'NGN',
      price: account.price || 0,
      availability: (account.status || 'available') === 'available' ? 'https://schema.org/InStock' : 'https://schema.org/SoldOut',
      itemCondition: 'https://schema.org/NewCondition'
    },
    image: [defaultImage]
  } : null;

  if (isLoading) {
    return (
      <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-luxury-200 rounded w-1/4 mb-8"></div>
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="h-96 bg-luxury-200 rounded-2xl"></div>
              <div className="space-y-4">
                <div className="h-12 bg-luxury-200 rounded w-3/4"></div>
                <div className="h-6 bg-luxury-200 rounded w-1/2"></div>
                <div className="h-32 bg-luxury-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <FiAlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-luxury-900 mb-2">Account Not Found</h2>
          <p className="text-luxury-600 mb-6">The account you're looking for doesn't exist or has been sold.</p>
          <Link to="/accounts">
            <button className="luxury-button">
              Browse Accounts
            </button>
          </Link>
        </div>
      </div>
    );
  }

  const Icon = platformIcons[account.socialMediaType] || FaFacebook;
  const color = platformColors[account.socialMediaType] || 'from-primary-500 to-secondary-500';

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={`buy verified social media account, ${account.socialMediaType || 'social media'} account, instant account delivery, secure social media account purchase`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={defaultImage} />
        <meta property="og:image:alt" content={`Verified ${account.socialMediaType} account on Nitro Logs`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={defaultImage} />
        <link rel="canonical" href={pageUrl} />
        {productSchema && <script type="application/ld+json">{JSON.stringify(productSchema)}</script>}
      </Helmet>

      <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 bg-linear-to-br from-luxury-50 via-white to-luxury-100">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="mb-6"
          >
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 text-luxury-600 hover:text-primary-600 transition-colors"
            >
              <FiArrowLeft className="w-5 h-5" />
              <span>Back to Accounts</span>
            </button>
          </motion.div>

          <div>
            {/* Details Section */}
            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="space-y-6"
            >
              {/* Title & Status */}
              <motion.div variants={fadeInUp}>
                <div className="flex items-center space-x-2 mb-2">
                  <div className={`w-8 h-8 rounded-lg bg-linear-to-r ${color} p-1.5 flex items-center justify-center`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-medium text-primary-600">{account.socialMediaType}</span>
                </div>
                <h1 className="text-3xl lg:text-4xl font-display font-bold text-luxury-900 mb-4">
                  {account.title}
                </h1>
                <div className="flex items-center space-x-2">
                  {/* status may be undefined during dev or older records; treat as available */}
                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                    (account.status || 'available') === 'available' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {(account.status || 'available') === 'available' ? 'Available' : 'Sold'}
                  </span>
                </div>
              </motion.div>

              {/* Price Card */}
              <motion.div
                variants={fadeInUp}
                className="glass p-6 rounded-2xl"
              >
                <div className="flex justify-between items-center mb-4">
                  <span className="text-luxury-600">Price</span>
                  <span className="text-3xl font-bold gradient-text">
                    ₦{account.price?.toLocaleString()}
                  </span>
                </div>
                <button
                  onClick={handlePurchase}
                  disabled={(account.status || 'available') !== 'available'}
                  className={`w-full luxury-button ${(account.status || 'available') !== 'available' ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {(account.status || 'available') === 'available' ? 'Purchase Now' : 'Account Sold'}
                </button>
              </motion.div>

              {/* Key Metrics */}
              <motion.div
                variants={fadeInUp}
                className="grid grid-cols-2 gap-4"
              >
                <GlassCard className="p-4 text-center">
                  <FiUsers className="w-6 h-6 text-primary-500 mx-auto mb-2" />
                  <span className="text-2xl font-bold text-luxury-900">
                    {account.followers?.toLocaleString() || '0'}
                  </span>
                  <p className="text-sm text-luxury-500">Followers</p>
                </GlassCard>
                <GlassCard className="p-4 text-center">
                  <FiClock className="w-6 h-6 text-secondary-500 mx-auto mb-2" />
                  <span className="text-2xl font-bold text-luxury-900">
                    {account.years}+
                  </span>
                  <p className="text-sm text-luxury-500">Years Old</p>
                </GlassCard>
                <GlassCard className="p-4 text-center">
                  <FiAward className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                  <span className="text-2xl font-bold text-luxury-900">Premium</span>
                  <p className="text-sm text-luxury-500">Quality</p>
                </GlassCard>
              </motion.div>

              {/* Description */}
              <motion.div variants={fadeInUp}>
                <h2 className="text-xl font-semibold text-luxury-900 mb-3">About this Account</h2>
                <p className="text-luxury-600 leading-relaxed">
                  This premium {account.socialMediaType} account comes with {account.followers?.toLocaleString()} real, 
                  active followers. The account is {account.years} years old with high engagement rates. 
                  Perfect for businesses and influencers looking to establish a strong presence on the platform.
                </p>
              </motion.div>

              {/* Features */}
              <motion.div variants={fadeInUp}>
                <h2 className="text-xl font-semibold text-luxury-900 mb-3">What's Included</h2>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    'Real active followers',
                    'High engagement rate',
                    'Original email access',
                    'Profile link included',
                    'Full account access',
                    '24/7 support',
                    'Instant delivery',
                    'Money-back guarantee'
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <FaCheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-luxury-600">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Verification Badges */}
              <motion.div
                variants={fadeInUp}
                className="flex flex-wrap gap-3"
              >
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center space-x-1">
                  <FiShield className="w-3 h-3" />
                  <span>Verified Account</span>
                </span>
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm flex items-center space-x-1">
                  <FiCheckCircle className="w-3 h-3" />
                  <span>Email Access</span>
                </span>
                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm flex items-center space-x-1">
                  <FiAward className="w-3 h-3" />
                  <span>Premium Quality</span>
                </span>
              </motion.div>

              {/* Report Button */}
              <motion.div variants={fadeInUp}>
                <button className="text-sm text-luxury-400 hover:text-red-500 transition-colors flex items-center space-x-1">
                  <FiFlag className="w-4 h-4" />
                  <span>Report this listing</span>
                </button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};